import type { CSSProperties, ReactNode } from "react";

/**
 * StatusIndicator - 상태 5종의 시각언어
 *
 * 계획서는 상태를 이모지(초록/노랑/빨강/흰/파란 원)로 적었다. UI 에서는 절대
 * 이모지를 쓰지 않는다. 폰트마다 모양과 크기가 달라지고, 12px
 * 에서 뭉개지고, 스크린리더가 "초록 원"이라고 읽는다.
 *
 * 대신 상태마다 고유한 실루엣을 준다. 색이 세 번째 신호가 되도록.
 *
 *   정상   ●  꽉 찬 원        disc
 *   주의   ◆  꽉 찬 마름모    diamond
 *   지연   ▲  꽉 찬 삼각형    triangle  (위를 향한다 = 올라와야 할 것)
 *   미착수 ○  빈 링           ring
 *   완료   v  체크 선         check
 *
 * 색을 완전히 지워도 다섯 개가 구분된다. 이게 색맹인 관리자가
 * 32행 테이블에서 지연을 찾아낼 수 있는 이유다.
 * 무게(면적)도 지연 > 주의 > 정상 > 완료 > 미착수 순으로 설계했다.
 */

export type StatusKey = "normal" | "warn" | "late" | "idle" | "done";

/** 상태 순위 - 정렬에 쓴다. 낮을수록 위로 올라온다. */
export const STATUS_RANK: Record<StatusKey, number> = {
  late: 0,
  warn: 1,
  normal: 2,
  idle: 3,
  done: 4,
};

type StatusMeta = {
  /** 화면에 그대로 나가는 한국어 라벨 */
  label: string;
  /** 폭이 없을 때 쓰는 1글자 */
  short: string;
  color: string;
  soft: string;
  shape: "disc" | "diamond" | "triangle" | "ring" | "check";
};

export const STATUS: Record<StatusKey, StatusMeta> = {
  normal: {
    label: "정상",
    short: "정",
    color: "var(--state-normal)",
    soft: "var(--state-normal-soft)",
    shape: "disc",
  },
  warn: {
    label: "주의",
    short: "주",
    color: "var(--state-warn)",
    soft: "var(--state-warn-soft)",
    shape: "diamond",
  },
  late: {
    label: "지연",
    short: "지",
    color: "var(--state-late)",
    soft: "var(--state-late-soft)",
    shape: "triangle",
  },
  idle: {
    label: "미착수",
    short: "미",
    color: "var(--state-idle)",
    soft: "var(--state-idle-soft)",
    shape: "ring",
  },
  done: {
    label: "완료",
    short: "완",
    color: "var(--state-done)",
    soft: "var(--state-done-soft)",
    shape: "check",
  },
};

/** 글리프 크기. row 는 36px 행 안에서 12px 텍스트와 나란히 선다. */
const GLYPH_PX = { row: 10, md: 12, hero: 20 } as const;
export type GlyphSize = keyof typeof GLYPH_PX;

const SHAPES: Record<StatusMeta["shape"], ReactNode> = {
  // 꽉 찬 원 - 가장 안정적인 실루엣. 아무 방향도 가리키지 않는다.
  disc: <circle cx="6" cy="6" r="4.1" fill="currentColor" />,
  // 마름모 - 모서리가 있어 원과 즉시 구분된다.
  diamond: <path d="M6 1.4 10.6 6 6 10.6 1.4 6Z" fill="currentColor" />,
  // 위를 향한 삼각형 - 경고 기호와 같은 방향이라 학습이 필요 없다.
  triangle: <path d="M6 1.3 11 10.4H1Z" fill="currentColor" />,
  // 빈 링 - 유일하게 속이 비어 있다. 밀도 높은 행에서도 즉시 읽힌다.
  ring: <circle cx="6" cy="6" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.6" />,
  // 체크 - 유일한 열린 선 마크. 배경색을 몰라도 그릴 수 있다.
  check: (
    <path
      d="M1.6 6.4 4.6 9.4 10.4 2.9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
};

/**
 * 단독 상태 글리프.
 *
 * 옆에 라벨 텍스트가 없으면(labelled=false) 스스로 role="img" +
 * aria-label 을 갖는다. 라벨이 함께 있으면 중복 낭독을 피하려고
 * aria-hidden 이 된다.
 */
export const StatusDot = ({
  status,
  size = "row",
  labelled = false,
  title,
  style,
}: {
  status: StatusKey;
  size?: GlyphSize;
  /** 바로 옆에 같은 뜻의 텍스트가 있는가 */
  labelled?: boolean;
  /** aria-label 을 덮어쓴다. 예: "금형 지연" */
  title?: string;
  style?: CSSProperties;
}) => {
  const meta = STATUS[status];
  const px = GLYPH_PX[size];
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 12 12"
      role={labelled ? undefined : "img"}
      aria-hidden={labelled ? true : undefined}
      aria-label={labelled ? undefined : (title ?? meta.label)}
      style={{ color: meta.color, flexShrink: 0, display: "block", ...style }}
    >
      {SHAPES[meta.shape]}
    </svg>
  );
};

/**
 * 글리프 + 한국어 라벨 배지.
 *
 * data-state-badge 가 붙어 있어서 prefers-contrast: more 에서
 * 소프트 틴트를 버리고 외곽선을 얻는다 (globals.css).
 */
export const StatusBadge = ({
  status,
  size = "md",
  label,
  style,
}: {
  status: StatusKey;
  size?: "sm" | "md";
  /** 라벨을 갈아끼운다. 예: "5일 지연" */
  label?: string;
  style?: CSSProperties;
}) => {
  const meta = STATUS[status];
  const sm = size === "sm";
  return (
    <span
      data-state-badge=""
      className={[
        "inline-flex items-center rounded-pill font-semibold whitespace-nowrap",
        sm ? "gap-s1 px-s2 py-px text-micro" : "gap-s2 px-s3 py-s05 text-caption",
      ].join(" ")}
      style={{ background: meta.soft, color: meta.color, ...style }}
    >
      <StatusDot status={status} size={sm ? "row" : "md"} labelled />
      {label ?? meta.label}
    </span>
  );
};

/**
 * 테이블 셀용 - 글리프만. 준비현황 5열(원소재/금형/T-O/생산/검사)
 * 처럼 한 행에 7개가 들어갈 때 쓴다.
 * 열 머리글이 무엇인지 말해주므로 aria-label 은 "열이름 상태" 로 짠다.
 */
export const StatusCell = ({ status, column }: { status: StatusKey; column: string }) => (
  <span className="flex items-center justify-center">
    <StatusDot status={status} size="row" title={`${column} ${STATUS[status].label}`} />
  </span>
);
